import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the "Dupe Order" entry point (PRO drawer button). Mirrors
 * `useCanCreateOrder.ts` — a single component id gates the whole dupe-order
 * surface (V1's `access_orders/dupepro` grant), because there's no per-field
 * owner variation to split on.
 */
export const ORDER_DUPE_COMPONENT_ID = 'order-dupe';

/**
 * Component id for duplicating an order that was NOT created today — V1's
 * `access_orders/dupeaftercreatedate` grant. Gated separately from
 * `ORDER_DUPE_COMPONENT_ID` because the everyday case is duplicating an order
 * you just entered; reaching back to an older order is a narrower, deliberately
 * granted capability. The backend re-checks this one itself (only it knows the
 * source's createdatetime and the DB-server clock to compare it against), so
 * the hook exists to pre-empt the 403 in the UI, not to enforce it.
 */
export const ORDER_DUPE_AFTER_CREATE_DATE_COMPONENT_ID = 'order-dupe-after-create-date';

/**
 * Whether the current user may open the Dupe Order flow at all.
 *
 * Resolved from the admin-configured component permission (permissions-mode
 * cog) crossed with the user's Azure AD groups. Ships **defaultDisabled**:
 * until an admin grants a group access on `order-dupe`, the dupe entry points
 * stay hidden for everyone (fallback level 1). The component ids are seeded by
 * migration 374. Uses the lenient context lookups inside
 * `useResolvedAccessLevel`, so in provider-less test/preview trees it returns
 * the fallback; production views take the real value as a prop (defaulting to
 * editable) so isolated renders are unaffected.
 */
export const useCanDupeOrder = (): boolean =>
  useResolvedAccessLevel(ORDER_DUPE_COMPONENT_ID, false, true) >= 2;

/**
 * Whether the current user may duplicate an order that was created before
 * today (V1 `dupeaftercreatedate` parity). Only meaningful once
 * `useCanDupeOrder` is already true — callers keep the dupe button enabled on
 * an older order when this is true and block it with the created-today message
 * otherwise.
 */
export const useCanDupeAfterCreateDate = (): boolean =>
  useResolvedAccessLevel(ORDER_DUPE_AFTER_CREATE_DATE_COMPONENT_ID, false, true) >= 2;
